import { Component, OnInit, ViewChild, AfterViewInit } from "@angular/core";
import { NgForm, Validators } from "@angular/forms";
import { ActivatedRoute } from "@angular/router";
import { catchError, map, finalize, flatMap } from "rxjs/operators";
import { of, throwError, Observable } from "rxjs";

import {
  TicketCutoffCreate,
  TicketCutoffOperationType,
  TicketCutoffType,
  SelectedAccountManagerFund,
  TicketCutoffException,
  TicketCutoffExceptionModel,
  TicketCutoffDefaultType,
  TicketCutoffDefaultModel,
  TicketCutoffHistory,
} from "../ticket-cutoff-type.model";
import { AccountManager } from "../../../model/domain/AccountManager";
import { SecurityService } from "../../../services/security.service";
import { CutoffService } from "../../../services/cutoff.service";
import { TicketService } from "../../../services/ticket.service";
import { Fund } from "../../../model/domain/Fund";
import { LoaderService } from "../../../services/loader.service";
import { SnackbarService } from "../../../services/snackbar.service";
import { TicketCutoffTypeEnum } from "../../../enum/cutoff-type.enum";
import { DateTime } from "luxon";

@Component({
  selector: "app-cutoff-create",
  templateUrl: "./cutoff-create.component.html",
  styleUrls: ["./cutoff-create.component.css"],
})
export class CutoffCreateComponent implements OnInit {
  constructor(
    private route: ActivatedRoute,
    private securityService: SecurityService,
    private cutoffService: CutoffService,
    private ticketService: TicketService,
    private loaderService: LoaderService,
    private snackBarService: SnackbarService
  ) {}

  @ViewChild("thisForm", { static: true }) public frm: NgForm;

  validators = {
    ticketCutoffType: (value) => this.coalesce(value) > 0,
    timeLimit: (value) =>
      this.isException()
        ? this.isTimeLimitValid()
        : this.coalesce(value).length > 0,
    justificative: (value) =>
      this.isException()
        ? this.coalesce(value).length >= 10
        : this.coalesce(value).length === 0,
  };

  model: TicketCutoffCreate;

  cutoffTypeId: number = null;
  cutoffOperationType: number = null;
  cutoffOperationTypeEnum: TicketCutoffOperationType;

  cutoffType: TicketCutoffType = null;
  cutoffTypeList: TicketCutoffType[] = null;

  accountManagerList: AccountManager[] = null;
  fundList: Fund[] = null;

  accountManagerId: number = null;
  fundId: number = null;

  selectedAccountManagerList = [];
  selectedFundList = [];

  registeredCutoffs: SelectedAccountManagerFund[] = null;
  accountManagerError: Array<{ id: number; message: string }> = null;

  history: TicketCutoffHistory[];

  isExceptionForDefault = false;

  ngOnInit() {
    this.createForm();

    if (this.accountManagerId != null && this.accountManagerId > 0) {
      this.fillSelectsWhenEdit();
    }

    this.getHistory();
  }

  fillSelectsWhenEdit() {
    this.isExceptionForDefault = true;
    this.selectedAccountManagerList.push(this.accountManagerId);

    this.selectedAccountManagerChanged();

    if (this.fundId) {
      this.selectedFundList.push(this.fundId);
      if (this.fundList == null) {
        this.ticketService
          .getFundsByAccountManagerIdList(this.selectedAccountManagerList)
          .subscribe((res) => {
            this.fundList = res;
            this.selectedFundChanged();
          });
      }
    }
  }

  createForm() {
    this.cutoffOperationType = Number(
      this.route.snapshot.paramMap.get("cutoffOperationType")
    );
    this.cutoffTypeId = Number(
      this.route.snapshot.paramMap.get("cutoffTypeId")
    );

    this.accountManagerId = Number(
      this.route.snapshot.paramMap.get("accountManagerId")
    );
    this.fundId = Number(this.route.snapshot.paramMap.get("fundId"));

    this.model = new TicketCutoffCreate();

    if (this.cutoffTypeId > 0) {
      this.cutoffService.getCutoffType(this.cutoffTypeId).subscribe((res) => {
        this.cutoffType = res;
      });
      this.model.ticketCutoffType = this.cutoffTypeId;
      this.getCutoffDefaultLimitTime();
    } else {
      this.cutoffService.listCutoffType().subscribe((res) => {
        this.cutoffTypeList = res;
      });
    }
  }

  submit() {
    this.register(this.frm)
      .pipe(
        catchError((err) => {
          this.loaderService.removeLoader();

          this.snackBarService.open(err, "OK", {
            verticalPosition: "top",
            horizontalPosition: "right",
          });
          return throwError(err);
        })
      )
      .subscribe(
        (r) => {
          this.loaderService.removeLoader();

          const msg = this.isDefault()
            ? "Novo horário padrão cadastrado."
            : "Novo horário de exceção cadastrado.";

          this.snackBarService.open(msg, "OK", {
            verticalPosition: "top",
            horizontalPosition: "right",
          });

          this.getHistory();
        },
        () => {}
      );
  }

  register(f: NgForm) {
    let data = null;

    const errors = this.getErrors();

    if (f.invalid || errors.length > 0) {
      if (errors.includes("justificative")) {
        return throwError("A justificativa deve conter pelo menos 10 letras.");
      }

      return throwError("Verifique os campos destacados.");
    }

    Object.values(f.controls).forEach((c) => {
      c.markAsTouched();
      c.updateValueAndValidity();
    });

    let method = null;

    if (this.cutoffOperationType === TicketCutoffOperationType.Default) {
      data = this.mapToDefault();
      method = this.cutoffService.registerDefaultInBatch(data);
    } else {
      data = this.mapToException();
      method = this.cutoffService.registerExceptionInBatch(data);
    }
    return method;
  }

  getAccountManagerList() {
    this.securityService.GetAccountManagers().subscribe((res) => {
      this.accountManagerList = res;
    });
  }

  getCutoffTypeList() {
    this.cutoffService.listCutoffType().subscribe((res) => {
      this.cutoffTypeList = res;
    });
  }

  getCutoffDefaultLimitTime() {
    this.getAccountManagerList();
    this.cutoffService
      .getCutoffDefaultByCutoffTypeAccManagerFund(
        this.model.ticketCutoffType,
        this.accountManagerId,
        this.fundId
      )
      .subscribe((res) => {
        this.model.timeLimit = this.cutoffService.dateTimeOffsetToTimeString(
          res.timeLimit
        );
      });

    if (this.cutoffOperationType === TicketCutoffOperationType.Default) {
      this.cutoffService
        .getRegisteredAccountManagerFundByCutoffType(
          this.model.ticketCutoffType
        )
        .subscribe((result) => {
          this.registeredCutoffs = result;
        });
    }
  }

  selectedAccountManagerChanged() {
    this.getFundsByAccountManager();

    if (this.model.selectedAccountManagerFundList != null) {
      for (const selectedAccountManagerItem of this
        .selectedAccountManagerList) {
        if (
          !this.model.selectedAccountManagerFundList.some(
            (x) => x.accountManagerId === selectedAccountManagerItem
          )
        ) {
          const newSelected = new SelectedAccountManagerFund();
          newSelected.accountManagerId = selectedAccountManagerItem;

          this.model.selectedAccountManagerFundList.push(newSelected);
          // Adiciona os AccManager que faltam
        }
      }

      for (const selectedAccountManagerItem of this.model
        .selectedAccountManagerFundList) {
        if (
          !this.selectedAccountManagerList.some(
            (x) => x === selectedAccountManagerItem.accountManagerId
          )
        ) {
          this.model.selectedAccountManagerFundList =
            this.model.selectedAccountManagerFundList.filter(function (obj) {
              return (
                obj.accountManagerId !==
                selectedAccountManagerItem.accountManagerId
              );
              // Remove do this.model.selectedAccountManagerFundList, o gestor que não está mais selecionado
            });
        }
      }
    } else {
      this.model.selectedAccountManagerFundList = [];
      for (const item of this.selectedAccountManagerList) {
        const newSelected = new SelectedAccountManagerFund();
        newSelected.accountManagerId = item;

        this.model.selectedAccountManagerFundList.push(newSelected);
      }
    }
  }

  selectedFundChanged() {
    for (const selectedFundId of this.selectedFundList) {
      const accountManagerId = this.fundList.find(
        (fl) => fl.id === selectedFundId
      ).accountManagerId;

      const accountManagerArray =
        this.model.selectedAccountManagerFundList.find(
          (si) => si.accountManagerId === accountManagerId
        );

      if (accountManagerArray.fundList != null) {
        if (!accountManagerArray.fundList.some((x) => x === selectedFundId)) {
          accountManagerArray.fundList.push(selectedFundId);
          // Adiciona o Fundo no AccountManager
        }
      } else {
        accountManagerArray.fundList = [];
        accountManagerArray.fundList.push(selectedFundId);
      }

      if (this.cutoffOperationType === TicketCutoffOperationType.Default) {
        this.removeAccountManagerError(accountManagerId);
      }
    }

    let itemsToRemove: number[];
    const itemsListed: number[] = [];
    for (const accountManagerList of this.model
      .selectedAccountManagerFundList) {
      if (
        accountManagerList.fundList != null &&
        accountManagerList.fundList.length > 0
      ) {
        for (const fundItem of accountManagerList.fundList) {
          itemsListed.push(fundItem);
        }
      }
    }
    itemsToRemove = itemsListed;
    for (const selectedFundItem of this.selectedFundList) {
      itemsToRemove = itemsToRemove.filter(function (obj) {
        return obj !== selectedFundItem;
      });
    }

    for (const accountManagerList of this.model
      .selectedAccountManagerFundList) {
      if (
        accountManagerList.fundList != null &&
        accountManagerList.fundList.length > 0
      ) {
        for (const itemToRemove of itemsToRemove) {
          accountManagerList.fundList = accountManagerList.fundList.filter(
            function (obj) {
              return obj !== itemToRemove;
            }
          );

          if (
            this.cutoffOperationType === TicketCutoffOperationType.Default &&
            (accountManagerList.fundList == null ||
              accountManagerList.fundList.length === 0)
          ) {
            this.validateAccountManagerOwnsDefault(
              accountManagerList.accountManagerId
            );
          }
        }
      }
    }
  }

  validateAccountManagerOwnsDefault(accountManagerId: number) {
    if (
      this.registeredCutoffs.find(
        (x) => x.accountManagerId === accountManagerId && x.fundList == null
      ) != null
    ) {
      const accountManagerName = this.accountManagerList.find(
        (x) => x.id === accountManagerId
      ).name;
      const timeLimit = this.registeredCutoffs.find(
        (x) => x.accountManagerId === accountManagerId && x.fundList == null
      ).defaultTimeLimit;

      const message = `O gestor ${accountManagerName} já possui um cutoff padrão às 
        ${this.cutoffService.dateTimeOffsetToTimeString(timeLimit)}.
      Retire o gestor ou selecione um fundo do mesmo.`;

      if (
        this.accountManagerError == null ||
        (this.accountManagerError != null &&
          !this.accountManagerError.some((x) => x.id === accountManagerId))
      ) {
        if (this.accountManagerError == null) {
          this.accountManagerError = new Array<{
            id: number;
            message: string;
          }>();
        }

        this.accountManagerError.push({ id: accountManagerId, message });
      }
    }
  }

  removeAccountManagerError(accountManagerId: number) {
    if (
      this.accountManagerError != null &&
      this.accountManagerError.length > 0
    ) {
      this.accountManagerError = this.accountManagerError.filter(function (
        obj
      ) {
        return obj.id !== accountManagerId;
      });
    }
  }

  removeSelectedFundsFromRemovedAccountManager($event) {
    const removedAccountManagerId = $event.value.id;

    const fundsIdsFromRemovedAccountManager = this.fundList.filter(
      (x) => x.accountManagerId === removedAccountManagerId
    );

    if (this.selectedFundList != null) {
      for (const fundIdToRemove of fundsIdsFromRemovedAccountManager) {
        this.selectedFundList = this.selectedFundList.filter(function (obj) {
          return obj !== fundIdToRemove.id;
        });
      }
    }
    if (this.cutoffOperationType === TicketCutoffOperationType.Default) {
      this.removeAccountManagerError(removedAccountManagerId);
    }
  }

  getFundsByAccountManager() {
    this.ticketService
      .getFundsByAccountManagerIdList(this.selectedAccountManagerList)
      .subscribe((res) => {
        if (this.cutoffOperationType === TicketCutoffOperationType.Exception) {
          this.fundList = res;
        } else {
          for (const itemToDisable of this.registeredCutoffs) {
            if (itemToDisable.fundList != null) {
              for (const fundToDisable of itemToDisable.fundList) {
                for (const fund of res) {
                  if (
                    fund.accountManagerId === itemToDisable.accountManagerId &&
                    fund.id === fundToDisable
                  ) {
                    res.find(
                      (x) =>
                        x.accountManagerId === itemToDisable.accountManagerId &&
                        x.id === fundToDisable
                    ).disabled = true;
                    // Desabilita no select, o fundo que já possui cutoff default daquele gestor
                  }
                }
              }
            } else {
              // O Gestor já possui um cutoff default sem fundo. Exibir mensagem para selecionar fundo.
              if (
                this.cutoffOperationType ===
                  TicketCutoffOperationType.Default &&
                this.model.selectedAccountManagerFundList.some(
                  (x) =>
                    x.accountManagerId === itemToDisable.accountManagerId &&
                    x.fundList == null
                )
              ) {
                this.validateAccountManagerOwnsDefault(
                  itemToDisable.accountManagerId
                );
              }
            }
          }
          this.fundList = res;
        }
      });
  }

  getHistory() {
    if (this.cutoffOperationType === TicketCutoffOperationType.Exception) {
      this.cutoffService
        .listExceptionHistory(
          this.cutoffTypeId,
          this.accountManagerId,
          this.fundId,
          this.model.selectedAccountManagerFundList
        )
        .subscribe((data) => {
          this.history = data;
        });
    }
  }

  clearSelectedAccountManagerFund() {
    this.selectedFundList = null;
    this.accountManagerError = null;
  }

  isDefault() {
    return this.cutoffOperationType === TicketCutoffOperationType.Default;
  }

  isException() {
    return this.cutoffOperationType === TicketCutoffOperationType.Exception;
  }

  disableSelect() {
    return this.isExceptionForDefault;
  }

  mapToException() {
    const data = new TicketCutoffExceptionModel();

    data.selectedAccountManagerFundList =
      this.model.selectedAccountManagerFundList;
    data.timeLimit = this.model.timeLimit;
    data.justificative = this.model.justificative;
    data.ticketCutoffType = this.model.ticketCutoffType;

    return data;
  }

  mapToDefault() {
    const data = new TicketCutoffDefaultModel();

    data.selectedAccountManagerFundList =
      this.model.selectedAccountManagerFundList;
    data.timeLimit = this.model.timeLimit;
    data.justificative = this.model.justificative;
    data.ticketCutoffType = this.model.ticketCutoffType;

    return data;
  }

  coalesce(value: any) {
    return value || "";
  }

  hasError(prop: string): boolean {
    return !this.validators[prop](this.model[prop]);
  }

  getErrors(): string[] {
    const validationErrors = [];

    Object.keys(this.validators).forEach((k) => {
      if (!this.validators[k](this.model[k])) {
        validationErrors.push(k);
      }
    });

    return validationErrors;
  }

  isTimeLimitValid() {
    const currentTime = DateTime.local();

    const hour = Number(this.model.timeLimit.toString().substr(0, 2));
    const minute = Number(this.model.timeLimit.toString().substr(3, 2));
    const limitTime = DateTime.local().set({
      hour: hour,
      minute: minute,
      second: 0,
    });

    if (limitTime < currentTime) {
      return false;
    }
    return true;
  }
}
